import { initializeApp } from "firebase/app";
import {
  getMessaging,
  getToken,
  onMessage,
  isSupported,
  Messaging,
} from "firebase/messaging";
import { getAuth, GoogleAuthProvider } from "firebase/auth";
import { REACT_APP_NOTIFICATION_VAPID_KEY } from "@/config/env";

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};
export const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();

export const isFirebaseSupported = async () => {
  try {
    return await isSupported();
  } catch (error) {
    console.error("Firebase is not supported:", error);
    return false;
  }
};

/** iPhone/iPad Safari (and Home Screen web apps) do not support FCM the way desktop Chrome does. */
export const isIosWeb = () => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  return (
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1)
  );
};

let messagingInstance: Messaging | null | undefined;

/** Never call getMessaging() at module load — it throws on iOS Safari and blanks sign-in. */
export function getFirebaseMessaging(): Messaging | null {
  if (messagingInstance !== undefined) return messagingInstance;
  if (typeof window === "undefined" || isIosWeb()) {
    messagingInstance = null;
    return null;
  }
  try {
    messagingInstance = getMessaging(app);
  } catch {
    messagingInstance = null;
  }
  return messagingInstance;
}

/** Lazy-safe export for existing callers. Use getFirebaseMessaging() at runtime. */
export const messaging: Messaging | null =
  typeof window === "undefined" ? null : getFirebaseMessaging();

const NOTIFICATION_PERMISSION_MS = 8000;

async function requestNotificationPermissionBounded(): Promise<NotificationPermission> {
  if (typeof Notification === "undefined") return "denied";
  if (Notification.permission !== "default") return Notification.permission;
  return Promise.race<NotificationPermission>([
    Notification.requestPermission(),
    new Promise<NotificationPermission>((resolve) =>
      setTimeout(() => resolve("default"), NOTIFICATION_PERMISSION_MS),
    ),
  ]);
}

/** FCM web is best-effort; login must still succeed if token fetch fails. */
export async function tryGetBrowserFcmToken(): Promise<string | null> {
  try {
    if (isIosWeb()) return null;
    if (typeof Notification === "undefined") return null;
    if (Notification.permission === "denied") return null;

    let perm: NotificationPermission = Notification.permission;
    if (perm === "default") {
      perm = await requestNotificationPermissionBounded();
    }
    if (perm !== "granted") return null;

    const firebaseSupported = await isFirebaseSupported();
    if (!firebaseSupported) return null;

    const instance = getFirebaseMessaging();
    if (!instance) return null;

    return await Promise.race([
      getToken(instance, {
        vapidKey: REACT_APP_NOTIFICATION_VAPID_KEY,
      }),
      new Promise<null>((resolve) => setTimeout(() => resolve(null), 4000)),
    ]);
  } catch {
    return null;
  }
}

export const getFCMToken = async (
  instance?: Messaging | null,
): Promise<string | null> => {
  try {
    if (isIosWeb()) return null;
    const firebaseSupported = await isFirebaseSupported();
    if (!firebaseSupported) {
      console.error("Firebase is not supported in this environment.");
      return null;
    }

    if (typeof Notification === "undefined") return null;

    if (Notification.permission === "granted") {
      console.log("Permission already granted.");
    } else if (Notification.permission === "denied") {
      console.error("Notification permission previously denied.");
      return null;
    } else {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") {
        console.error("Notification permission denied.");
        return null;
      }
      console.log("Permission granted after request.");
    }

    const resolved = instance ?? getFirebaseMessaging();
    if (!resolved) return null;

    const token = await getToken(resolved, {
      vapidKey: REACT_APP_NOTIFICATION_VAPID_KEY,
    });

    if (token) {
      localStorage.setItem("fcmToken", token);
      return token;
    }
    console.error("No FCM token available.");
    return null;
  } catch (error) {
    console.error("Error generating FCM token:", error);
    return null;
  }
};

export const onMessageListener = () => {
  const instance = getFirebaseMessaging();
  if (!instance) return Promise.resolve(null);
  return new Promise((resolve) => {
    onMessage(instance, (payload) => {
      resolve(payload);
    });
  });
};
